import { LoadingSpinner } from '@/components/loading-error';
import { useCoachStore } from '@/store/coach';
import api from '@/utils/ky';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export function MeetingList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const coachStore = useCoachStore();

  // Get userId from URL params or store
  const userId = searchParams.get('userId') || coachStore.userId;

  useEffect(() => {
    // If no userId available, redirect to landing
    if (!userId) {
      toast.error('User session not found');
      navigate('/meeting');
      return;
    }

    // If userId from URL params, update store
    if (
      searchParams.get('userId') &&
      searchParams.get('userId') !== coachStore.userId
    ) {
      coachStore.setUser({
        userId: searchParams.get('userId')!,
        deviceId: coachStore.deviceId || '',
        name: coachStore.userName || '',
        function: coachStore.function || 'other',
      });
    }
  }, [userId, navigate, coachStore]);

  const {
    data: meetings = [],
    isLoading,
    error,
  } = useQuery<Array<{ id: string; title: string; date: string; attendee: string }>>({
    queryKey: ['meetings', userId],
    queryFn: () =>
      api
        .get('api/meeting/list', {
          searchParams: { userId },
        })
        .json(),
    enabled: !!userId,
    retry: 1,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!meetings?.length) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="text-center">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            No meetings found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            We couldn't find any recent 1:1 meetings with transcripts.
          </p>
          <button
            onClick={() => navigate('/meeting')}
            className="mt-6 inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-12 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Recent 1:1 Meetings</h1>
        <button
          onClick={() => navigate('/meeting')}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          Back to Home
        </button>
      </div>

      <div className="space-y-4">
        {meetings.map((meeting) => (
          <motion.div
            key={meeting.id}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() =>
              navigate(`/meeting/feedback/${meeting.id}?userId=${userId}`)
            }
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">{meeting.title}</h3>
                <p className="text-gray-600">
                  {new Date(meeting.date).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
                <p className="text-sm text-gray-500">{meeting.attendee}</p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
